/* eslint-disable unicorn/no-document-cookie */
import React, { useMemo } from 'react';
import { startsWith } from 'lodash';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { BlockUi, Box, CardContent, Grid, Paper, Typography } from '@availity/element';
import { getSpecificEnv } from '@availity/env-var';
import { useEffectAsync } from '@availity/hooks';
import { FRUser } from '@forgerock/javascript-sdk';

import { AvailityHeaderLogo, Footer } from '@/components';
import { useAppContext } from '@/context';

const currentEnv = getSpecificEnv();

const Logout = ({ ...props }) => {
  const { type } = props;
  const { setIsAuthenticated, setEmail, setUser } = useAppContext();
  const navigate = useNavigate();

  const logout = async () => {
    try {
      /**
       * Logout and clear existing, stored data
       * Note that the setAuthentication method below calls the FRUser.logout
       * method, ensuring the access artifacts are revoked on ForgeRock.
       */
      setIsAuthenticated(false);
      setEmail('');
      setUser('');

      // Allow for enough time to communicate the action
      setTimeout(async () => {
        await FRUser.logout();

        // manually set F5 cookie to id cloud value post-logout
        if (currentEnv === 'prod') {
          document.cookie = `BIGipServer~edgeservice-north~north-prd-apps-pool-ag-443=rd3050o00000000000000000000ffff0a473034o443; path=/; Secure;`;
          document.cookie = `BIGipServer~edgeservice-north~north-prd-apps-pool-dt-443=rd3050o00000000000000000000ffff0a463034o443; path=/; Secure;`;
        } else if (currentEnv === 'qap') {
          document.cookie = `BIGipServer~edgeservice-north~north-apps-qap-pool-ag-443=rd3050o00000000000000000000ffff0a4830dfo443; path=/; Secure;`;
        } else if (startsWith(currentEnv, 'q')) {
          document.cookie = `BIGipServer~edgeservice-north~north-apps-qua-pool-ag-443=rd3050o00000000000000000000ffff0a4830deo443; path=/; Secure;`;
        } else if (startsWith(currentEnv, 't')) {
          document.cookie = `BIGipServer~edgeservice-north~north-apps-tst-pool-ag-443=rd3050o00000000000000000000ffff0a4830ddo443; path=/; Secure;`;
        }

        // handle navigation back to login page
        navigate('/login');
      }, 1000);
    } catch (error) {
      console.log(error);
    }
  };

  useEffectAsync(async () => {
    await logout();
  }, []);

  const displayText = useMemo(() => {
    let output;
    switch (type) {
      case 'redirect': {
        output = 'Redirecting to Login...';
        break;
      }
      default: {
        output = 'Logging out of your account...';
      }
    }

    return output;
  }, []);

  return (
    <Grid
      id="logout-page-grid-container"
      role="main"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid
        id="logout-page-content-grid"
        container
        display="flex"
        flexDirection="column"
        alignItems="center"
        flexGrow={1}
      >
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Paper variant="elevation" elevation={6} sx={{ width: { xs: '350px', sm: '500px' } }}>
            <CardContent>
              <BlockUi
                tag="div"
                message={
                  <Typography variant="subtitle1">
                    <Box sx={{ fontWeight: 'bold' }}>{displayText}</Box>
                  </Typography>
                }
                blocking
              >
                <Box sx={{ minHeight: '130px' }} />
              </BlockUi>
            </CardContent>
          </Paper>
        </Grid>
      </Grid>
      <Footer />
    </Grid>
  );
};

Logout.propTypes = {
  type: PropTypes.string,
};

export default Logout;
